import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useNavigation } from '../../../hooks/useNavigation';
import { A11Y } from '../../../utils/constants';

const Nav = styled.nav`
  background-color: rgba(0, 0, 0, 0.8);
  padding: ${({ theme }) => theme.spacing.sm} 0;
  position: relative;
`;

const MenuContainer = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  position: relative;
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.2rem;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.white};
    outline-offset: 2px;
  }
`;

const MenuList = styled.ul<{ $isOpen?: boolean }>`
  list-style: none;
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin: 0;
  padding: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.95);
    padding: ${({ theme }) => theme.spacing.md};
    z-index: ${({ theme }) => theme.zIndex.dropdown};
    box-shadow: ${({ theme }) => theme.shadows.card};
  }
`;

const MenuItem = styled.li`
  margin: 0;
`;

const MenuLink = styled(Link)<{ $isActive?: boolean }>`
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: 4px;
  transition: ${({ theme }) => theme.transitions.fast};
  display: block;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    text-decoration: none;
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.white};
    outline-offset: 2px;
    text-decoration: none;
  }
  
  ${props => props.$isActive && `
    background-color: rgba(255, 255, 255, 0.2);
  `}
`;

const LoadingMessage = styled.div`
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: #ffcdd2;
  padding: ${({ theme }) => theme.spacing.sm};
  text-align: center;
  font-size: 0.9rem;
`;

export const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { navigation, loading, error } = useNavigation();
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Close menu when clicking outside (for mobile)
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen && !(event.target as Element).closest('nav')) {
        closeMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Close menu on route change
  React.useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  if (loading) {
    return (
      <Nav>
        <MenuContainer>
          <LoadingMessage>Loading navigation...</LoadingMessage>
        </MenuContainer>
      </Nav>
    );
  }

  if (error || !navigation) {
    return (
      <Nav>
        <MenuContainer>
          <ErrorMessage>
            {error || 'Navigation unavailable'}
          </ErrorMessage>
        </MenuContainer>
      </Nav>
    );
  }

  return (
    <Nav>
      <MenuContainer>
        <MenuToggle 
          onClick={toggleMenu}
          aria-label={A11Y.MENU_TOGGLE_TEXT}
          aria-expanded={isMenuOpen}
        >
          Menu
        </MenuToggle>
        <MenuList $isOpen={isMenuOpen} role="menubar">
          {navigation.menu.map((item) => (
            <MenuItem key={item.id} role="none">
              <MenuLink
                to={item.slug}
                $isActive={location.pathname === item.slug}
                onClick={closeMenu}
                role="menuitem"
              >
                {item.title}
              </MenuLink>
            </MenuItem>
          ))}
        </MenuList>
      </MenuContainer>
    </Nav>
  );
};

export default Navigation;
